import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AuthLayoutWrapper } from '@/components/auth/AuthLayoutWrapper';
import logoImage from '@/assets/prsnl-logo.svg';
import authIllustration from '@/assets/auth/auth-illustration.jpg';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { resetPasswordForEmail } from '@/services/data/auth';

interface ForgotPasswordScreenProps {
  readonly onBack: () => void;
}

export function ForgotPasswordScreen({ onBack }: ForgotPasswordScreenProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email) || email.length > 254) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }
    setIsLoading(true);
    try {
      const result = await resetPasswordForEmail(email, window.location.origin);
      if (!result.success) {
        throw result.error;
      }
      setEmailSent(true);
    } catch (error: unknown) {
      console.error('Password reset error:', error);
      toast.error('Erreur lors de l\'envoi de l\'email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayoutWrapper illustrationSrc={authIllustration}>
      <div className="text-center relative">
        <Link
          to="/login"
          className="absolute -top-16 left-0 p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Retour"
        >
          <ArrowLeft className="h-5 w-5 text-white/70" />
        </Link>

        <img src={logoImage} alt="PRSNL" className="w-40 h-auto mx-auto mb-8 brightness-0 invert" />
        
        {emailSent ? (
          <div className="space-y-6">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-2">
                Email envoyé
              </h2>
              <p className="text-white/60 text-sm">
                Vérifiez votre boîte mail et suivez les instructions pour réinitialiser votre mot de passe.
              </p>
            </div>

            <Button
              asChild
              className="w-full h-12 rounded-xl bg-white text-black hover:bg-white/90 font-medium transition-all"
            >
              <Link to="/login">Retour à la connexion</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <h1 className="text-2xl font-semibold text-white mb-2">
                Mot de passe oublié
              </h1>
              <p className="text-white/60 text-sm">
                Entrez votre email pour recevoir un lien de réinitialisation
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-3 text-left">
                <label htmlFor="email" className="text-white text-sm block mb-2">
                  Adresse email
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={isLoading}
                  className="bg-white/5 backdrop-blur-sm border-white/10 text-white rounded-xl h-12 px-5 text-base placeholder:text-white/30"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 rounded-xl bg-white text-black hover:bg-white/90 font-medium transition-all"
              >
                {isLoading ? 'Envoi en cours...' : 'Envoyer le lien'}
              </Button>
            </form>
          </>
        )}
      </div>
    </AuthLayoutWrapper>
  );
}